import { IconDefinition, faDisplayCode, faFloppyDisk } from '@fortawesome/pro-light-svg-icons';
import { faAdd } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Typography,
  Divider,
  Theme,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import styled from 'styled-components';
import useCommonStyles from 'views/useCommonStyles';
import ConfigMenu from './ConfigMenu';
import { cloneDeep } from 'lodash';
import { useConfig } from 'context/ConfigContext';
import {
  CONFIRMATION,
  actions,
  configType,
  CARD_DEFINITION_KEY,
  MOBILE_ONLY_CARD_DEFINITION_KEYS,
} from 'utils/Utils';
import { faLinkSimple, faLinkSimpleSlash } from '@fortawesome/pro-regular-svg-icons';
import _ from 'lodash';
import { useConfirmDialog } from 'context/ConfirmContext';
import { useTheme } from '@material-ui/core/styles';

interface Props {
  title: string;
  icon?: IconDefinition;
  buttonText?: string;
  configKey?: string;
  onSave?: () => void;
  simulate?: boolean;
  openModals?: (val: boolean) => void;
  setHeader?: (val: string) => void;
  enableAddButton?: boolean;
  enableSaveButton?: boolean;
  style?: string;
  showDivider?: boolean;
  showLinkUnlink?: boolean;
  headerStyle?: string;
  titleStyle?: string;
  saveInProgress?: boolean;
}

const PageHeader = ({
  title,
  icon,
  buttonText,
  configKey,
  onSave,
  simulate,
  openModals,
  setHeader,
  enableAddButton = false,
  enableSaveButton = false,
  style,
  showDivider = false,
  showLinkUnlink = false,
  headerStyle = '',
  titleStyle = '',
  saveInProgress = false,
}: Props) => {
  const classes = useCommonStyles();
  const theme = useTheme();
  const { config, setConfig, configDetails, setConfigDetails, handleConfigTypeChange } =
    useConfig();

  const handleLinkAndUnlink = () => {
    const configCopy = cloneDeep(config);
    const webConfig = configCopy.webUIConfig.aggregateUICustomizations;
    const mobileConfig = configCopy.mobileUIConfig.aggregateUICustomizations;
    if (configKey && webConfig && mobileConfig) {
      const [commandName, nodeName] = configKey?.split('.');

      if (nodeName === actions.EDITOR_ACTIONS) {
        mobileConfig[commandName].editorActions = webConfig[commandName].tableActions;
      } else {
        let webNode = webConfig[commandName][nodeName];
        if (nodeName === CARD_DEFINITION_KEY) {
          for (const mobileOnlyCardDefKey of MOBILE_ONLY_CARD_DEFINITION_KEYS) {
            const mobileOnlyCardDefKeyData =
              mobileConfig[commandName][nodeName][mobileOnlyCardDefKey];
            if (mobileOnlyCardDefKeyData) {
              webNode = { ...webNode, [mobileOnlyCardDefKey]: mobileOnlyCardDefKeyData };
            }
          }
        }
        mobileConfig[commandName][nodeName] = webNode;
      }
      if (configDetails[configKey]?.type === configType.ALL.type) {
        setConfigDetails(configKey, configType.WEB);
      } else {
        setConfigDetails(configKey, configType.ALL);
      }
      setConfig(configCopy);
    }
  };
  const tempDescription = configKey as string;
  const onLinkClick = () => {
    if (configDetails[tempDescription] !== configType.ALL) {
      handleConfigTypeChange(tempDescription, configType.ALL, configDetails[tempDescription]?.type);
      return;
    }
    handleConfigTypeChange(tempDescription, configType.WEB, configDetails[tempDescription]?.type);
  };
  const componentStyles = useStyles();
  const { openConfirmation } = useConfirmDialog();
  const linkAndUnlinkHandler = _.includes(['symbolLegend', 'fabActions'], configKey)
    ? undefined
    : handleLinkAndUnlink;

  const isAllType = configDetails[tempDescription]?.type === configType.ALL.type;
  const confirmationObj = CONFIRMATION.linking[isAllType ? 'unlink' : 'link'];

  const handleLinking = async () => {
    const action = linkAndUnlinkHandler ? linkAndUnlinkHandler : onLinkClick;
    const resp = await openConfirmation(confirmationObj);
    if (resp === 'confirm') {
      action();
    }
  };

  return (
    <>
      <Box
        className={style || [classes.titleBarContainer, headerStyle].join(' ')}
        style={{ background: theme.palette.background.paper, color: theme.palette.text.primary }}
      >
        <Box className={classes.titleRow}>
          <Typography
            variant="h2"
            className={[classes.header, titleStyle].join(' ')}
            style={{ color: theme.palette.text.primary }}
          >
            {title}
          </Typography>
          {configKey && <ConfigMenu definitionName={configKey} />}
        </Box>
        <ButtonContainer>
          {!showLinkUnlink && (
            <Button onClick={handleLinking} className={componentStyles.buttonStyles}>
              <FontAwesomeIcon
                icon={isAllType ? faLinkSimpleSlash : faLinkSimple}
                className={componentStyles.titleIcon}
              />
              <Typography className={componentStyles.iconStyle}>{confirmationObj.title}</Typography>
            </Button>
          )}
          {simulate && (
            <Button
              variant="outlined"
              startIcon={<FontAwesomeIcon icon={faDisplayCode} />}
              classes={{
                root: classes.simulatorBtnRoot,
                label: classes.simulatorBtnText,
              }}
              onClick={onSave ? onSave : undefined}
            >
              Simulate
            </Button>
          )}
          {enableAddButton && (
            <Box className={classes.buttonContainer}>
              <Button
                disableElevation
                variant="outlined"
                color="primary"
                className={classes.addButton}
                onClick={() => {
                  openModals && openModals(true);
                  setHeader && setHeader('Add');
                }}
                startIcon={<FontAwesomeIcon icon={faAdd} />}
              >
                Add
              </Button>
            </Box>
          )}
          {!enableSaveButton && (
            <Button
              variant="outlined"
              startIcon={<FontAwesomeIcon icon={icon ? icon : faFloppyDisk} />}
              classes={{
                root: classes.saveBtnRoot,
                label: classes.saveBtnText,
              }}
              onClick={onSave ? onSave : undefined}
              disabled={saveInProgress}
              color="primary"
            >
              {buttonText}
            </Button>
          )}
        </ButtonContainer>
      </Box>
      {showDivider && <Divider style={styles.dividers} />}
    </>
  );
};

export default PageHeader;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
`;
const styles = {
  dividers: { position: 'sticky' as const, top: 87, zIndex: 999 },
};
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    titleIcon: {
      width: 25,
      height: 20,
      color: theme.palette.text.secondary,
    },
    buttonStyles: {
      position: 'absolute',
      right: 120,
      borderRadius: 5,
      border: `1px solid ${theme.palette.action.selected}`,
      background: theme.palette.background.paper,
      boxShadow: `0px 2px 4px 0px ${theme.palette.action.selected}`,
      height: 40,
      padding: '3px 16px 2px 16px',
      color: theme.palette.text.primary,
    },
    iconStyle: {
      color: theme.palette.text.secondary,
      fontSize: 16,
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: '100%',
      marginLeft: 6,
    },
  })
);
